import { Injectable } from '@angular/core';

import { ticketConverter } from '../tickets/ticket.converter';

import {
  IContentNotification,
  IContractNotification,
  IDocumentNotification,
  IKeyValue,
  isIContentNotification,
  isIContractNotification,
  isIDocumentNotification,
  isITicketNotification,
  isPushMessage,
  ITicketNotification,
  NotificationData,
  NotificationType,
  PushMessage,
  ResidentEmailNotificationActionType,
  ResidentNotificationActionType,
  TicketIssueType,
} from '../../models';

@Injectable()
export abstract class NotificationBaseService {
  /**
   * Redirects the user to the correct content page based on the message data provided.
   *
   * @param notificationData the message data containing the type and any relevant ids/names
   */
  protected redirectToContent(notificationData: NotificationData): void {
    if (!notificationData) {
      return;
    }

    switch (notificationData.notificationType) {
      case ResidentEmailNotificationActionType.NEW_ANNOUNCEMENT:
      case ResidentNotificationActionType.NEW_CONTENT:
        if (isIContentNotification(notificationData) && notificationData.contentId) {
          this.navigateToAnnouncementDetails(notificationData.contentId);
        } else {
          console.error('Missing contentId for NEW_ANNOUNCEMENT type');
        }
        break;
      case ResidentEmailNotificationActionType.NEW_TICKET_MESSAGE:
      case ResidentNotificationActionType.TICKET_MESSAGE:
        if (isITicketNotification(notificationData) && notificationData.ticketId) {
          this.navigateToTicketChat(notificationData.ticketId, notificationData.issueType);
        } else {
          console.error('Missing ticketId for NEW_TICKET_MESSAGE');
        }
        break;
      case ResidentEmailNotificationActionType.NEW_DOCUMENT:
      case ResidentNotificationActionType.DOCUMENT_SHARED:
        if (isIDocumentNotification(notificationData) && notificationData.documentId) {
          this.navigateToDocument(notificationData.documentId);
        } else {
          console.error('Missing documentId, documentType, or documentName for NEW_DOCUMENT type');
        }
        break;
      case ResidentEmailNotificationActionType.NEW_TICKET_STATUS:
      case ResidentNotificationActionType.TICKET_STATUS_CHANGE:
        if (
          isITicketNotification(notificationData) &&
          notificationData.ticketId &&
          notificationData.issueType
        ) {
          this.navigateToTicketDetails(notificationData.ticketId, notificationData.issueType);
        } else {
          console.error('Missing ticketId or issueType for TICKET_STATUS_CHANGE type');
        }
        break;
      case ResidentEmailNotificationActionType.NEW_LEGAL_TEXT:
      case ResidentNotificationActionType.LEGAL_TEXT:
        // Consent flow is handled automatically by ConsentInterceptor
        break;
      case ResidentNotificationActionType.PAYMENT_SHARED:
        // case ResidentEmailNotificationActionType.XXX TODO: Email notification for payment is missing
        this.navigateToPayment();
        break;
      case ResidentEmailNotificationActionType.NEW_OFFER:
      case ResidentNotificationActionType.NEW_CONTENT_OFFER:
        if (isIContentNotification(notificationData) && notificationData.contentId) {
          this.navigateToOfferDetails(notificationData.contentId);
        } else {
          console.error('Missing contentId for NEW_OFFER type');
        }
        break;
      case ResidentEmailNotificationActionType.NEW_NEWS:
      case ResidentNotificationActionType.NEW_CONTENT_NEWS:
        if (isIContentNotification(notificationData) && notificationData.contentId) {
          this.navigateToNewsDetails(notificationData.contentId);
        } else {
          console.error('Missing contentId for NEW_NEWS type');
        }
        break;
      case ResidentEmailNotificationActionType.NEW_ADDRESS:
      case ResidentNotificationActionType.CONTRACT_END_NEW_ADDRESS:
        if (isIContractNotification(notificationData) && notificationData.contractId) {
          this.navigateToAddressChange(notificationData.contractId);
        } else {
          console.error('Missing contractId for CONTRACT_END_NEW_ADDRESS type');
        }
        break;
      default:
        console.error(`Unknown notification type: ${notificationData.notificationType}`);
        this.navigateToDashboard();
    }
  }

  protected createNotificationData(
    notificationType: NotificationType,
    variables: string[] | PushMessage
  ): NotificationData {
    const isPushMessageData = isPushMessage(variables);

    switch (notificationType) {
      case ResidentEmailNotificationActionType.NEW_NEWS:
      case ResidentNotificationActionType.NEW_CONTENT_NEWS:
        return {
          notificationType,
          contentId: isPushMessageData ? variables?.data?.contentId : variables[0],
        } as IContentNotification;
      case ResidentEmailNotificationActionType.NEW_ANNOUNCEMENT:
      case ResidentNotificationActionType.NEW_CONTENT:
        return {
          notificationType,
          contentId: isPushMessageData ? variables?.data?.contentId : variables[0],
        } as IContentNotification;
      case ResidentEmailNotificationActionType.NEW_OFFER:
      case ResidentNotificationActionType.NEW_CONTENT_OFFER:
        return {
          notificationType,
          contentId: isPushMessageData ? variables?.data?.contentId : variables[0],
        } as IContentNotification;
      case ResidentEmailNotificationActionType.NEW_DIGITAL_DOCUMENTS_CONSENT:
        return {
          notificationType,
          contentId: isPushMessageData ? variables?.data?.contentId : variables[0],
        } as IContentNotification;
      case ResidentEmailNotificationActionType.NEW_LEGAL_TEXT:
      case ResidentNotificationActionType.LEGAL_TEXT:
        return {
          notificationType,
          contentId: isPushMessageData ? variables?.data?.contentId : variables[0],
        } as IContentNotification;
      case ResidentEmailNotificationActionType.NEW_DOCUMENT:
      case ResidentNotificationActionType.DOCUMENT_SHARED:
        return {
          notificationType,
          documentId: isPushMessageData ? variables?.data?.documentId : variables[0],
        } as IDocumentNotification;
      case ResidentEmailNotificationActionType.NEW_TICKET_MESSAGE:
      case ResidentNotificationActionType.TICKET_MESSAGE:
        return {
          notificationType,
          ticketId: isPushMessageData ? variables?.data?.ticketId : variables[0],
          issueType: ticketConverter.normalizeIssueType(
            isPushMessageData ? variables?.data?.issueType : variables[1]
          ),
        } as ITicketNotification;
      case ResidentEmailNotificationActionType.NEW_TICKET_STATUS:
      case ResidentNotificationActionType.TICKET_STATUS_CHANGE:
        return {
          notificationType,
          ticketId: isPushMessageData ? variables?.data?.ticketId : variables[0],
          issueType: ticketConverter.normalizeIssueType(
            isPushMessageData ? variables?.data?.issueType : variables[1]
          ),
        } as ITicketNotification;
      case ResidentEmailNotificationActionType.NEW_ADDRESS:
      case ResidentNotificationActionType.CONTRACT_END_NEW_ADDRESS:
        return {
          notificationType,
          contractId: isPushMessageData ? variables?.data?.contractId : variables[0],
          contractExternalId: isPushMessageData
            ? variables?.data?.contractExternalId
            : variables[1],
          propertyAddress: isPushMessageData ? variables?.data?.propertyAddress : variables[2],
        } as IContractNotification;
      default:
        return null;
    }
  }

  protected abstract navigateTo(route: string[], queryParams: IKeyValue): void;
  protected abstract navigateToAnnouncementDetails(contentId: string): void;
  protected abstract navigateToTicketChat(ticketId: string, issueType: TicketIssueType): void;
  protected abstract navigateToTicketDetails(ticketId: string, issueType: TicketIssueType): void;
  protected abstract navigateToDocument(documentId: string): void;
  protected abstract navigateToLegalText(): void;
  protected abstract navigateToPayment(): void;
  protected abstract navigateToOfferDetails(contentId: string): void;
  protected abstract navigateToNewsDetails(contentId: string): void;
  protected abstract navigateToAddressChange(contractId: string): void;
  protected abstract navigateToDashboard(): void;
}
